import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getRemoteConfigSmartRecommendation } from '~store/insiderTracking/thunks';

const CACHE_DURATION = 60 * 60 * 1000;

const useSmartRecommendations = ({ placements = '' }) => {
  const dispatch = useDispatch();
  const lastFetchTime = useRef(null);

  const {
    listSmartRecommendationProducts,
    smartRecommendationCampaignPlacements,
    status,
  } = useSelector((state) => state.insiderTracking);

  const campaignID = useMemo(
    () => smartRecommendationCampaignPlacements?.[placements]?.campaignID,
    [smartRecommendationCampaignPlacements, placements],
  );

  const smartRecommendationProducts = useMemo(() => {
    if (!Array.isArray(listSmartRecommendationProducts) || !campaignID) {
      return listSmartRecommendationProducts;
    }

    return listSmartRecommendationProducts
      .filter((itemRecommend) =>
        campaignID.includes(itemRecommend.recomendationId),
      )
      .map((itemRecommend) => itemRecommend.data)
      .flat();
  }, [listSmartRecommendationProducts, campaignID]);

  const isLoading = useMemo(
    () =>
      status.listSmartRecommendationProducts === 'loading' ||
      status.smartRecommendationCampaignPlacements === 'loading',
    [status],
  );

  useEffect(() => {
    const now = Date.now();
    const shouldFetch =
      !lastFetchTime.current || now - lastFetchTime.current >= CACHE_DURATION;

    if (shouldFetch) {
      lastFetchTime.current = now;
      dispatch(getRemoteConfigSmartRecommendation());
    }
  }, []);

  return useMemo(
    () => ({
      products: smartRecommendationProducts,
      placements: smartRecommendationCampaignPlacements?.[placements],
      isLoading,
    }),
    [
      smartRecommendationProducts,
      smartRecommendationCampaignPlacements,
      isLoading,
      placements,
    ],
  );
};

useSmartRecommendations.propTypes = {
  placements: PropTypes.string,
};

export default useSmartRecommendations;
